import { hasFeature } from '@/lib/license';
import { isDesktop } from '@/lib/fileSystem';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useNavGuard } from '@/contexts/NavGuardContext';

/**
 * Small "PRO" badge shown on feature cards and tool buttons.
 */
export function ProBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white shadow-sm ${className}`}
      style={{ background: 'linear-gradient(135deg, #F97066, #FB923C)' }}
    >
      Pro
    </span>
  );
}

/**
 * Feature gate wrapper.
 *
 * - If user has access → renders children
 * - If not → renders fallback (default: blurred preview + upgrade prompt)
 *
 * Usage:
 *   <ProGate feature="invoice">
 *     <InvoiceBuilder />
 *   </ProGate>
 */
export function ProGate({ feature, children, fallback }) {
  const navigate = useNavigate();
  const guard = useNavGuard();
  const desktop = isDesktop();

  if (hasFeature(feature)) {
    return children;
  }

  if (fallback) {
    return fallback;
  }

  const goUpgrade = () => {
    // Route through the nav guard so a Viewer with unsaved edits surfaces
    // its UnsavedWarningDialog before sending the user off-page.
    guard(() => {
      if (desktop) {
        // Desktop has no Stripe checkout; direct users to the registration
        // code flow that the desktop Help menu launches.
        navigate('/account');
      } else {
        navigate('/account/billing');
      }
    });
  };

  return (
    <div className="relative min-h-[60vh] flex items-center justify-center">
      {/* Blurred preview background */}
      <div className="absolute inset-0 overflow-hidden rounded-xl">
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="text-gray-300 text-9xl font-bold select-none opacity-30">PRO</div>
        </div>
      </div>

      {/* Upgrade prompt */}
      <div className="relative z-10 bg-white rounded-2xl shadow-xl border border-gray-200 p-8 max-w-md text-center">
        <div className="w-14 h-14 bg-brand-blue rounded-xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Feature</h3>
        <p className="text-gray-600 mb-6">
          This feature is available with Scrivar Pro. Upgrade to unlock all advanced tools.
        </p>
        <button
          type="button"
          onClick={goUpgrade}
          className="w-full px-6 py-3 bg-brand-blue text-white font-semibold rounded-xl hover:bg-brand-dark transition-all shadow-md hover:shadow-lg"
        >
          Upgrade to Pro
        </button>
        <p className="text-xs text-gray-400 mt-3">
          {desktop
            ? <>Already have a Pro code? Go to <span className="font-medium">Help &gt; Enter Registration Code</span>.</>
            : <>Already have a code? <button type="button" onClick={() => navigate('/account')} className="text-brand-blue underline hover:no-underline">Activate it here</button>.</>
          }
        </p>
      </div>
    </div>
  );
}

/**
 * Lock icon overlay for individual tool buttons (e.g. annotation tools in Lite).
 */
export function ProLock({ feature, children, className = '' }) {
  const locked = !hasFeature(feature);
  const navigate = useNavigate();
  const guard = useNavGuard();
  const desktop = isDesktop();

  return (
    <div className={`relative ${className}`}>
      {children}
      {locked && (
        <button
          type="button"
          className="absolute inset-0 flex items-center justify-center bg-white/60 rounded cursor-pointer"
          title={desktop
            ? 'Upgrade to Scrivar Pro. Enter a code in Help > Enter Registration Code.'
            : 'Upgrade to Scrivar Pro to unlock this tool.'}
          aria-label="Upgrade to Pro to unlock"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Route through the nav guard so Viewer surfaces its
            // UnsavedWarningDialog when the user has pending edits.
            guard(() => navigate(desktop ? '/account' : '/account/billing'));
          }}
        >
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </button>
      )}
    </div>
  );
}
